import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getAllUsers, deleteMultipleUsers, deleteAllBotUsers,
  getUserTypeLabel, getUserTypeBadgeColor,
  getDeletedUsers, liftDeletionRestrictionByDocId, removeDeletedUserRecord,
  bulkAdjustBattery, bulkSetBattery,
} from '../services/userService';
import { sendBulkDM } from '../services/dmService';
import { useAuthStore } from '../stores/authStore';
import { formatTimestamp, isUserActiveNow } from '../utils/time';
import { downloadCSV, downloadJSON } from '../utils/export';
import { coordsToFrequency } from '../utils/frequency';
import UserDetailModal from '../components/common/UserDetailModal';
import DeletedUserDetailModal from '../components/common/DeletedUserDetailModal';
import type { User, DeletedUser } from '../types';

type SortKey = 'nickname' | 'email' | 'battery' | 'createdAt' | 'lastActiveAt';
type FilterType = 'all' | 'regular' | 'bot' | 'admin' | 'operator' | 'online';

export default function UsersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { adminUser } = useAuthStore();
  const [mainTab, setMainTab] = useState<'active' | 'deleted'>('active');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const initFilter = (location.state as any)?.filter as FilterType | undefined;
  const [filterType, setFilterType] = useState<FilterType>(() => initFilter || (localStorage.getItem('admin_users_filter') as FilterType) || 'all');
  const [sortKey, setSortKey] = useState<SortKey>(() => (localStorage.getItem('admin_users_sortKey') as SortKey) || 'createdAt');
  const [sortAsc, setSortAsc] = useState(() => localStorage.getItem('admin_users_sortAsc') === 'true');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showUserModal, setShowUserModal] = useState<string | null>(null);

  // Bulk action modal
  const [bulkAction, setBulkAction] = useState<'battery' | 'dm' | null>(null);
  const [bulkBatteryAmount, setBulkBatteryAmount] = useState('');
  const [bulkDmContent, setBulkDmContent] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Deleted users
  const [deletedUsers, setDeletedUsers] = useState<DeletedUser[]>([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [deletedSearch, setDeletedSearch] = useState('');
  const [showDeletedDetail, setShowDeletedDetail] = useState<DeletedUser | null>(null);

  useEffect(() => { loadUsers(); }, []);

  async function loadUsers() {
    setLoading(true);
    try { setUsers(await getAllUsers()); }
    finally { setLoading(false); }
  }

  async function loadDeletedUsers() {
    setDeletedLoading(true);
    try { setDeletedUsers(await getDeletedUsers()); }
    finally { setDeletedLoading(false); }
  }

  useEffect(() => { if (mainTab === 'deleted') loadDeletedUsers(); }, [mainTab]);

  const filtered = useMemo(() => {
    let result = users;
    if (search) {
      const kw = search.toLowerCase();
      result = result.filter(u =>
        u.nickname?.toLowerCase().includes(kw) ||
        u.email?.toLowerCase().includes(kw) ||
        u.uid.toLowerCase().includes(kw)
      );
    }
    if (filterType !== 'all') {
      result = result.filter(u => {
        if (filterType === 'admin') return u.isAdmin;
        if (filterType === 'operator') return u.isOperator;
        if (filterType === 'bot') return u.isBot;
        if (filterType === 'online') return isUserActiveNow(u.lastActiveAt);
        return !u.isAdmin && !u.isOperator && !u.isBot;
      });
    }
    result.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'nickname') cmp = (a.nickname || '').localeCompare(b.nickname || '');
      else if (sortKey === 'email') cmp = (a.email || '').localeCompare(b.email || '');
      else if (sortKey === 'battery') cmp = (a.battery || 0) - (b.battery || 0);
      else if (sortKey === 'createdAt') cmp = (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0);
      else if (sortKey === 'lastActiveAt') cmp = (a.lastActiveAt?.toMillis?.() || 0) - (b.lastActiveAt?.toMillis?.() || 0);
      return sortAsc ? cmp : -cmp;
    });
    return result;
  }, [users, search, filterType, sortKey, sortAsc]);

  const filteredDeleted = useMemo(() => {
    if (!deletedSearch) return deletedUsers;
    const kw = deletedSearch.toLowerCase();
    return deletedUsers.filter(d =>
      d.email?.toLowerCase().includes(kw) ||
      d.nickname?.toLowerCase().includes(kw) ||
      d.uid?.toLowerCase().includes(kw)
    );
  }, [deletedUsers, deletedSearch]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  }

  useEffect(() => { localStorage.setItem('admin_users_sortKey', sortKey); }, [sortKey]);
  useEffect(() => { localStorage.setItem('admin_users_sortAsc', String(sortAsc)); }, [sortAsc]);
  useEffect(() => { localStorage.setItem('admin_users_filter', filterType); }, [filterType]);

  const lastSelectedRef = useRef<string | null>(null);

  function toggleSelect(uid: string, e?: React.MouseEvent) {
    const s = new Set(selected);
    if (e?.shiftKey && lastSelectedRef.current) {
      const ids = filtered.map(u => u.uid);
      const lastIdx = ids.indexOf(lastSelectedRef.current);
      const curIdx = ids.indexOf(uid);
      if (lastIdx >= 0 && curIdx >= 0) {
        const [start, end] = lastIdx < curIdx ? [lastIdx, curIdx] : [curIdx, lastIdx];
        for (let i = start; i <= end; i++) s.add(ids[i]);
      }
    } else {
      s.has(uid) ? s.delete(uid) : s.add(uid);
    }
    lastSelectedRef.current = uid;
    setSelected(s);
  }

  function selectAll() {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(u => u.uid)));
  }

  async function handleDeleteSelected() {
    if (!selected.size || !confirm(`${selected.size}명의 사용자를 삭제하시겠습니까?\n(백업 → Firestore 삭제 → Auth 삭제)`)) return;
    const result = await deleteMultipleUsers([...selected]);
    alert(`완료: ${result.succeeded}명 삭제, ${result.failed}명 실패`);
    setSelected(new Set());
    loadUsers();
  }

  async function handleDeleteAllBots() {
    if (!confirm('모든 봇 사용자를 삭제하시겠습니까?')) return;
    const count = await deleteAllBotUsers();
    alert(`${count}개의 봇 사용자가 삭제되었습니다.`);
    loadUsers();
  }

  async function handleBulkBatteryAdjust(mode: 'give' | 'deduct' | 'set') {
    const amount = parseInt(bulkBatteryAmount);
    if (isNaN(amount) || amount < 0) { alert('유효한 금액을 입력하세요.'); return; }
    const uids = [...selected];
    if (!confirm(`${uids.length}명에게 배터리 ${mode === 'give' ? '지급' : mode === 'deduct' ? '차감' : '절대값 설정'} (${amount.toLocaleString()} 셀)?`)) return;
    setBulkProcessing(true);
    try {
      if (mode === 'set') await bulkSetBattery(uids, amount, adminUser?.uid || 'admin');
      else await bulkAdjustBattery(uids, mode === 'give' ? amount : -amount, adminUser?.uid || 'admin');
      alert(`${uids.length}명 처리 완료`);
      setBulkAction(null);
      setBulkBatteryAmount('');
      loadUsers();
    } finally { setBulkProcessing(false); }
  }

  async function handleBulkDM() {
    if (!bulkDmContent.trim() || !adminUser) return;
    const uids = [...selected];
    const recipients = uids.map(uid => {
      const u = users.find(x => x.uid === uid);
      return { uid, nickname: u?.nickname || 'Unknown' };
    });
    if (!confirm(`${recipients.length}명에게 쪽지를 보내시겠습니까?`)) return;
    setBulkProcessing(true);
    try {
      const count = await sendBulkDM({
        senderId: adminUser.uid,
        senderNickname: adminUser.nickname,
        recipients,
        content: bulkDmContent,
      });
      alert(`${count}명에게 쪽지 전송 완료`);
      setBulkAction(null);
      setBulkDmContent('');
    } finally { setBulkProcessing(false); }
  }

  function exportUsers(format: 'csv' | 'json') {
    const data = filtered.map(u => ({
      uid: u.uid, nickname: u.nickname, email: u.email, type: getUserTypeLabel(u),
      battery: u.battery, gender: u.gender,
      frequency: u.lat != null && u.lng != null ? coordsToFrequency(u.lat, u.lng) : 'N/A',
      createdAt: formatTimestamp(u.createdAt),
      lastActiveAt: formatTimestamp(u.lastActiveAt),
    }));
    if (format === 'csv') downloadCSV(data, 'users.csv');
    else downloadJSON(data, 'users.json');
  }

  async function handleLiftRestriction(du: DeletedUser) {
    if (!confirm(`"${du.nickname}" (${du.email})의 탈퇴 재가입 제한을 해제하시겠습니까?`)) return;
    await liftDeletionRestrictionByDocId(du.docId);
    loadDeletedUsers();
  }

  async function handleRemoveRecord(du: DeletedUser) {
    if (!confirm(`"${du.nickname}" 탈퇴 기록을 완전히 삭제하시겠습니까?`)) return;
    await removeDeletedUserRecord(du.docId);
    loadDeletedUsers();
  }

  function getRestrictionStatus(du: DeletedUser): { restricted: boolean; daysRemaining: number; label: string; color: string } {
    if (!du.deletedAt || du.deletedAt === 0) {
      return { restricted: false, daysRemaining: 0, label: '제한 해제됨', color: 'bg-green-100 text-green-700' };
    }
    const cooldownDays = 7; // default, ideally read from appConfig
    const cooldownMs = cooldownDays * 24 * 60 * 60 * 1000;
    const elapsed = Date.now() - du.deletedAt;
    if (elapsed >= cooldownMs) {
      return { restricted: false, daysRemaining: 0, label: '제한 만료', color: 'bg-gray-100 text-gray-600' };
    }
    const remaining = Math.max(1, Math.ceil((cooldownMs - elapsed) / (24 * 60 * 60 * 1000)));
    return { restricted: true, daysRemaining: remaining, label: `${remaining}일 남음`, color: 'bg-red-100 text-red-700' };
  }

  const SortHeader = ({ k, label }: { k: SortKey; label: string }) => (
    <th
      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-900"
      onClick={() => toggleSort(k)}
    >
      {label} {sortKey === k ? (sortAsc ? '\u25B2' : '\u25BC') : ''}
    </th>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-xl sm:text-2xl font-bold">사용자 관리</h1>
        <div className="flex gap-1 sm:gap-2 flex-wrap">
          {mainTab === 'active' && (
            <>
              <button onClick={() => exportUsers('csv')} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">CSV</button>
              <button onClick={() => exportUsers('json')} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">JSON</button>
              <button onClick={handleDeleteAllBots} className="px-3 py-1.5 text-sm bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100">봇 전체 삭제</button>
              <button onClick={loadUsers} className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-100">새로고침</button>
            </>
          )}
          {mainTab === 'deleted' && (
            <button onClick={loadDeletedUsers} className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-100">새로고침</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        <button onClick={() => setMainTab('active')} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${mainTab === 'active' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          활성 사용자 ({users.length})
        </button>
        <button onClick={() => setMainTab('deleted')} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${mainTab === 'deleted' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          탈퇴 사용자 {deletedUsers.length > 0 ? `(${deletedUsers.length})` : ''}
        </button>
      </div>

      {/* ===== DELETED USERS TAB ===== */}
      {mainTab === 'deleted' && (
        <div>
          <input
            value={deletedSearch}
            onChange={e => setDeletedSearch(e.target.value)}
            placeholder="이메일, 닉네임, UID 검색..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm mb-4"
          />

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">닉네임</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">이메일</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">성별</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">탈퇴일</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">사유</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">재가입 제한</th>
                  <th className="px-3 py-3 w-40"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {deletedLoading ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">로딩 중...</td></tr>
                ) : filteredDeleted.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">탈퇴 사용자 없음</td></tr>
                ) : filteredDeleted.map(du => {
                  const status = getRestrictionStatus(du);
                  const deletedDate = du.deletedAt && du.deletedAt > 0
                    ? new Date(du.deletedAt).toLocaleString('ko-KR')
                    : 'N/A';
                  return (
                    <tr key={du.docId} className="hover:bg-gray-50 cursor-pointer" onClick={() => setShowDeletedDetail(du)}>
                      <td className="px-3 py-2 font-medium">{du.nickname || '-'}</td>
                      <td className="px-3 py-2 text-gray-500">{du.email || '-'}</td>
                      <td className="px-3 py-2 text-gray-500">{du.gender || '-'}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{deletedDate}</td>
                      <td className="px-3 py-2 text-gray-500">{du.reason || '-'}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${status.color}`}>{status.label}</span>
                      </td>
                      <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1 whitespace-nowrap">
                          {status.restricted && (
                            <button onClick={() => handleLiftRestriction(du)}
                              className="px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100">
                              제한 해제
                            </button>
                          )}
                          <button onClick={() => handleRemoveRecord(du)}
                            className="px-2 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100">
                            기록 삭제
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t border-gray-200 text-sm text-gray-500">
              {filteredDeleted.length}명 / 전체 {deletedUsers.length}명
            </div>
          </div>
        </div>
      )}

      {/* ===== ACTIVE USERS TAB ===== */}
      {mainTab === 'active' && (
        <div>
          {/* Filters */}
          <div className="flex gap-3 mb-4">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="닉네임, 이메일, UID 검색..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
            />
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value as FilterType)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">전체 ({users.length})</option>
              <option value="online">온라인</option>
              <option value="regular">일반</option>
              <option value="bot">봇</option>
              <option value="admin">관리자</option>
              <option value="operator">운영자</option>
            </select>
          </div>

          {selected.size > 0 && (
            <div className="mb-3 p-3 bg-blue-50 rounded-lg space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-blue-800">{selected.size}명 선택</span>
                <button onClick={handleDeleteSelected} className="px-2 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100">선택 삭제</button>
                <button onClick={() => setBulkAction('battery')} className="px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100">배터리 관리</button>
                <button onClick={() => setBulkAction('dm')} className="px-2 py-1 text-xs bg-purple-50 text-purple-700 border border-purple-200 rounded hover:bg-purple-100">쪽지 일괄 보내기</button>
                <button onClick={() => setSelected(new Set())} className="px-2 py-1 text-xs text-gray-500 border border-gray-300 rounded hover:bg-gray-100">선택 해제</button>
              </div>

              {/* Bulk battery */}
              {bulkAction === 'battery' && (
                <div className="p-3 bg-white rounded-lg border border-gray-200 space-y-2">
                  <h4 className="text-sm font-semibold">일괄 배터리 관리 ({selected.size}명)</h4>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input type="number" value={bulkBatteryAmount} onChange={e => setBulkBatteryAmount(e.target.value)} placeholder="셀 수량" className="w-32 px-2 py-1 border border-gray-300 rounded text-sm" />
                    <button onClick={() => handleBulkBatteryAdjust('give')} disabled={bulkProcessing} className="px-3 py-1 text-sm bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50">지급</button>
                    <button onClick={() => handleBulkBatteryAdjust('deduct')} disabled={bulkProcessing} className="px-3 py-1 text-sm bg-orange-50 text-orange-700 border border-orange-200 rounded hover:bg-orange-100 disabled:opacity-50">차감</button>
                    <button onClick={() => handleBulkBatteryAdjust('set')} disabled={bulkProcessing} className="px-3 py-1 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50">절대값변경</button>
                    <button onClick={() => setBulkAction(null)} className="px-3 py-1 text-sm text-gray-500 border border-gray-300 rounded">닫기</button>
                  </div>
                </div>
              )}

              {/* Bulk DM */}
              {bulkAction === 'dm' && (
                <div className="p-3 bg-white rounded-lg border border-gray-200 space-y-2">
                  <h4 className="text-sm font-semibold">일괄 쪽지 보내기 ({selected.size}명)</h4>
                  <p className="text-xs text-gray-500">발신자: {adminUser?.nickname} ({adminUser?.email})</p>
                  <textarea value={bulkDmContent} onChange={e => setBulkDmContent(e.target.value)} placeholder="쪽지 내용..." rows={3} className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none" />
                  <div className="flex gap-2">
                    <button onClick={handleBulkDM} disabled={bulkProcessing || !bulkDmContent.trim()} className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">전송</button>
                    <button onClick={() => setBulkAction(null)} className="px-3 py-1.5 text-sm text-gray-500 border border-gray-300 rounded">닫기</button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="whitespace-nowrap">
                    <th className="px-3 py-3 w-10">
                      <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={selectAll} />
                    </th>
                    <SortHeader k="nickname" label="닉네임" />
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500">성별</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500"><button onClick={() => toggleSort('email')}>이메일 {sortKey === 'email' ? (sortAsc ? '\u25B2' : '\u25BC') : ''}</button></th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">유형</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500"><button onClick={() => toggleSort('battery')}>배터리 {sortKey === 'battery' ? (sortAsc ? '\u25B2' : '\u25BC') : ''}</button></th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500">상태</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500"><button onClick={() => toggleSort('lastActiveAt')}>최근활동 {sortKey === 'lastActiveAt' ? (sortAsc ? '\u25B2' : '\u25BC') : ''}</button></th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500"><button onClick={() => toggleSort('createdAt')}>가입일 {sortKey === 'createdAt' ? (sortAsc ? '\u25B2' : '\u25BC') : ''}</button></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">로딩 중...</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">사용자 없음</td></tr>
                  ) : filtered.map(user => (
                    <tr key={user.uid} className="hover:bg-gray-50 cursor-pointer whitespace-nowrap" onClick={() => setShowUserModal(user.uid)}>
                      <td className="px-3 py-2" onClick={e => { e.stopPropagation(); toggleSelect(user.uid, e); }}>
                        <input type="checkbox" checked={selected.has(user.uid)} readOnly />
                      </td>
                      <td className="px-3 py-2 font-medium text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full overflow-hidden border border-gray-200 shrink-0 flex items-center justify-center bg-gray-100">
                            {user.profileImageUrl ? (
                              <img src={user.profileImageUrl} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs">{user.gender === 'male' ? '👨' : user.gender === 'female' ? '👩' : '🧑'}</span>
                            )}
                          </div>
                          <span>{user.nickname || '-'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center text-sm">{user.gender === 'male' ? '♂' : user.gender === 'female' ? '♀' : '?'}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">{user.email || '-'}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getUserTypeBadgeColor(user)}`}>{getUserTypeLabel(user)}</span>
                      </td>
                      <td className="px-3 py-2 text-sm font-mono text-right">{(user.battery || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`w-2 h-2 inline-block rounded-full ${isUserActiveNow(user.lastActiveAt) ? 'bg-green-500' : 'bg-gray-300'}`} />
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">{formatTimestamp(user.lastActiveAt, 'relative')}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{formatTimestamp(user.createdAt, 'short')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 text-sm text-gray-500">
              {filtered.length}명 표시 / 전체 {users.length}명
            </div>
          </div>
        </div>
      )}

      {showUserModal && <UserDetailModal uid={showUserModal} onClose={() => setShowUserModal(null)} onDeleted={loadUsers} />}
      {showDeletedDetail && <DeletedUserDetailModal user={showDeletedDetail} onClose={() => setShowDeletedDetail(null)} />}
    </div>
  );
}
